import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { TablePriceDisplay } from '@/components/ui-v2/inputs/table/table-price-display';
import { TablePriceInput } from '@/components/ui-v2/inputs/table/table-price-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCellValidationRuleError } from '@/hooks/use-cell-validation-rule-error';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import {
  cn,
  formatCurrency,
  getCurrentValue,
  getDeltaSign,
  getDeltaValue,
  getMyLastValue,
  getVatLabelKey,
  hasPreviousVersion,
} from '@/lib/utils';
import { rangeFilterFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { evolutionInsightsState } from '@/states/evolution-insights.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { storeAtom } from '@/states/store.state';
import { KPIComparison } from '@/types/kpi-comparison';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon, TrendingDown, TrendingUp } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface ChoosePriceCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function ChoosePriceCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ChoosePriceCellContentProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const kpiComparison = useAtomValue(kpiComparisonState);
  const evolutionInsights = useAtomValue(evolutionInsightsState);
  const user = useUser();
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'choosePrice');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.choosePrice?.toFixed(2);
    if (typeof value === 'undefined' || value === null) return '—';
    return formatCurrency(parseFloat(value), store);
  };

  const getParentValue = () => {
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.choosePrice?.toFixed(2);
      if (typeof value === 'undefined' || value === null) return;
      subRowsValues.push(parseFloat(value));
    });
    if (subRowsValues.length === 0) return '—';
    const min = formatCurrency(Math.min(...subRowsValues), store);
    const max = formatCurrency(Math.max(...subRowsValues), store);
    return min === max ? max : `${min} - ${max}`;
  };

  const getParentComparedValue = () => {
    if (
      kpiComparison !== KPIComparison.DISCOUNT_HIGHER &&
      kpiComparison !== KPIComparison.DISCOUNT_LOWER
    ) {
      return '—';
    }
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const comparedItem = evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === subRow.original.id
      );
      if (
        comparedItem?.choosePrice !== undefined &&
        comparedItem.choosePrice !== null
      ) {
        subRowsValues.push(comparedItem.choosePrice / 100);
      }
    });
    if (subRowsValues.length === 0) return '—';
    const min = formatCurrency(Math.min(...subRowsValues), store);
    const max = formatCurrency(Math.max(...subRowsValues), store);
    return min === max ? max : `${min} - ${max}`;
  };

  const emptyErrorState = getTableCellErrorState({
    row,
    field: 'choosePrice',
    errorType: 'empty',
  });
  const hasRuleError = useCellValidationRuleError(row, 'choosePrice');
  const errorState = {
    hasError: emptyErrorState.hasError || hasRuleError,
    isEmpty: emptyErrorState.isEmpty,
    errorMessage: emptyErrorState.errorMessage,
  };

  const getPreviousItem = () => {
    if (kpiComparison === KPIComparison.DISCOUNT_HIGHER) {
      return evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === row.original.id
      )?.choosePrice;
    }
    if (kpiComparison === KPIComparison.DISCOUNT_LOWER) {
      return evolutionInsights?.campaignItemsWithEvolution.find(
        (item) => item.dealItemId === row.original.id
      )?.choosePrice;
    }
  };

  const value = getValue();
  const hasComparison =
    kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
    kpiComparison === KPIComparison.DISCOUNT_LOWER;

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="choosePrice"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="flex h-full w-full flex-row">
          <div className={cn('h-full', hasComparison ? 'w-[160px]' : 'w-full')}>
            {isEditing ? (
              <TablePriceInput
                value={row.original.activeVersion.choosePrice ?? null}
                onChange={async (value) => {
                  await handleCellEdit(
                    row.original.activeVersion.id,
                    'choosePrice',
                    value,
                    !isParent
                  );
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isOfferLocked}
                autoFocus={isEditing}
                hasError={errorState.hasError}
              />
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="h-full w-full"
                    onClick={() =>
                      !isParent &&
                      handleCellClick(
                        row.original.activeVersion.id,
                        'choosePrice'
                      )
                    }
                  >
                    <TablePriceDisplay
                      value={
                        errorState.isEmpty
                          ? null
                          : isParent
                            ? getParentValue()
                            : value
                      }
                      isEmpty={errorState.isEmpty}
                      hasError={errorState.hasError}
                      errorMessage={errorState.errorMessage}
                      isRecentlyModified={isRecentlyModified}
                      isEditable={!isOfferLocked}
                      isParent={isParent}
                    >
                      {!isParent &&
                        user?.role &&
                        hasPreviousVersion(
                          row.original,
                          'choosePrice',
                          user?.role
                        ) &&
                        getDeltaSign(row.original, 'choosePrice', user?.role) >
                          0 && (
                          <TrendingUp className="absolute right-[-11px] top-[-11px] h-4 w-4 rounded-full bg-green-100 p-[2px] text-green-700" />
                        )}
                      {!isParent &&
                        user?.role &&
                        hasPreviousVersion(
                          row.original,
                          'choosePrice',
                          user?.role
                        ) &&
                        getDeltaSign(row.original, 'choosePrice', user?.role) <
                          0 && (
                          <TrendingDown className="absolute right-[-11px] top-[-11px] h-4 w-4 rounded-full bg-red-100 p-[2px] text-red-700" />
                        )}
                    </TablePriceDisplay>
                  </div>
                </TooltipTrigger>
                {!isParent &&
                  user?.role &&
                  hasPreviousVersion(
                    row.original,
                    'choosePrice',
                    user?.role
                  ) && (
                    <TooltipContent>
                      <p className="text-sm font-semibold">
                        {t('kpi.choose_price_modified')}
                      </p>
                      <p>
                        <span className="text-gray-500">
                          {t('general.previous')} :
                        </span>{' '}
                        <span>
                          {formatCurrency(
                            getMyLastValue(
                              row.original,
                              'choosePrice',
                              user?.role
                            ),
                            store
                          )}
                        </span>
                      </p>
                      <p>
                        <span className="text-gray-500">
                          {t('general.current')} :
                        </span>{' '}
                        <span>
                          {formatCurrency(
                            getCurrentValue(row.original, 'choosePrice'),
                            store
                          )}
                        </span>
                      </p>
                      <p className="font-semibold">
                        <span className="text-gray-500">
                          {t('general.gap')} :
                        </span>{' '}
                        <span
                          className={cn({
                            'text-green-600':
                              getDeltaSign(
                                row.original,
                                'choosePrice',
                                user?.role
                              ) > 0,
                            'text-red-600':
                              getDeltaSign(
                                row.original,
                                'choosePrice',
                                user?.role
                              ) < 0,
                          })}
                        >
                          {getDeltaSign(
                            row.original,
                            'choosePrice',
                            user?.role
                          ) > 0
                            ? '+'
                            : ''}
                          {formatCurrency(
                            getDeltaValue(
                              row.original,
                              'choosePrice',
                              user?.role
                            ),
                            store
                          )}
                        </span>
                      </p>
                    </TooltipContent>
                  )}
              </Tooltip>
            )}
          </div>
          {hasComparison && (
            <div
              className={cn(
                'flex-1 text-right',
                !isOfferLocked && !isParent
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-50'
              )}
              onClick={() =>
                !isParent &&
                handleCellClick(row.original.activeVersion.id, 'choosePrice')
              }
            >
              <div
                className={`flex h-full w-full items-center justify-end px-2 text-sm`}
              >
                <span>
                  {!isParent
                    ? formatCurrency((getPreviousItem() ?? 0) / 100, store)
                    : getParentComparedValue()}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefChoosePrice = ({
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const kpiComparison = useAtomValue(kpiComparisonState);
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'choosePrice';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'choosePrice',
      filterFn: rangeFilterFn,
      accessorFn: (row) => row.activeVersion?.choosePrice ?? 0,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="flex w-full cursor-pointer flex-row font-semibold">
          <div className="flex w-[160px] cursor-pointer font-semibold">
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <InfoIcon className="h-4 w-4" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('kpi.choose_price_tooltip')}</p>
                </TooltipContent>
              </Tooltip>{' '}
              <div
                className="flex flex-1 items-center gap-2"
                onClick={() => setColumnSort(accessorKey)}
              >
                {t('kpi.choose_price')} ({t(getVatLabelKey(store))})
                <SortIcon pointer="choosePrice" />
              </div>
            </div>
          </div>
          {(kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
            kpiComparison === KPIComparison.DISCOUNT_LOWER) && (
            <div
              className="flex w-full flex-1 items-center justify-end gap-2 text-right"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.choose_price_compared')}
              <SortIcon pointer="choosePrice" />
            </div>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <ChoosePriceCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size:
        kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
        kpiComparison === KPIComparison.DISCOUNT_LOWER
          ? 340
          : 180,
    }),
    [
      t,
      store,
      kpiComparison,
      setColumnSort,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
      handleBlur,
    ]
  );
};

export default useColumnDefChoosePrice;
